import type { AdGenJob, AdGenStageDetail, ListJobsResponse, CreateJobRequest } from './types'
import { API_CONFIG } from '@/shared/config'

const API_URL = API_CONFIG.baseUrl

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getAuthToken } = await import('@trafiflow/shared-ui')
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const adGeneratorService = {
  /**
   * Create a new ad generation job from a URL.
   */
  async createJob(request: CreateJobRequest): Promise<AdGenJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/ad-generator/jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List all ad generation jobs for the current tenant.
   */
  async listJobs(): Promise<ListJobsResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/ad-generator/jobs`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to list jobs: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a single job with full stage status and (if complete) winner/variants.
   */
  async getJob(jobId: string): Promise<AdGenJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/ad-generator/jobs/${jobId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get detail for a specific stage including its artifact.
   */
  async getStageDetail(jobId: string, stageName: string): Promise<AdGenStageDetail> {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${API_URL}/ad-generator/jobs/${jobId}/stages/${stageName}`,
      { method: 'GET', headers }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get stage: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Cancel a running job.
   */
  async cancelJob(jobId: string): Promise<void> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/ad-generator/jobs/${jobId}/cancel`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to cancel job: ${response.status}`)
    }
  },

  /**
   * Delete a job and all its artifacts.
   */
  async deleteJob(jobId: string): Promise<void> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/ad-generator/jobs/${jobId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to delete job: ${response.status}`)
    }
  },
} as const
